// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import copyCalculatorConfiguration from "@stoneage/App/Handlers/JettingApp/copyCalculatorConfiguration";
import shareCalculatorConfiguration from "@stoneage/App/Handlers/JettingApp/shareCalculatorConfiguration ";
import { Panel } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    onEmailClick?: () => void;
    onCopyClick?: () => void;
    panels: Panel[];
    calculatorId: string;
    extendedStyles?: ButtonPresentationProps;
    units: string | null;
    onlyCalculateOnButton: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    location: getLocation(state),
    calculatorIdFromState: state.xJettingAppCalculators.calculatorId || "",
});

const mapDispatchToProps = {
    shareCalculatorConfiguration,
    copyCalculatorConfiguration,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface JettinAppActionsStyles {
    wrapper?: InjectableCss;
    emailLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
    copyLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    container?: InjectableCss;
    recipientEmailTextField?: TextFieldPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const jettinAppActionsStyles: JettinAppActionsStyles = {};

const JettinAppActions = ({
    extendedStyles,
    onCopyClick,
    onEmailClick,
    panels,
    shareCalculatorConfiguration,
    calculatorId,
    copyCalculatorConfiguration,
    location,
    calculatorIdFromState,
    units,
    onlyCalculateOnButton,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);

    const [styles] = useState(() => mergeToNew(jettinAppActionsStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [recipientEmail, setRecipientEmail] = React.useState("");
    const [recipientEmailError, setRecipientEmailError] = React.useState("");
    const [inProgress, setInProgress] = React.useState(false);
    const [isCopyInProgress, setIsCopyInProgress] = React.useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const onClickShare = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const requiredFieldMessage = translate("This is a required field");
    const emailFieldMessage = translate("Enter a valid email address");
    const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const disableSubmitButton = !!recipientEmailError || !recipientEmail;

    const shareButtonClickHandler = () => {
        if (recipientEmailError) {
            return;
        }
        setInProgress(true);
        const suggestJets = parseBoolean(localStorage.getItem("jettingAppSuggestJets") || "false");
        shareCalculatorConfiguration({
            id: calculatorId,
            emailTo: recipientEmail,
            jettingCalculatorConfig: {
                panels,
                changeUnits: false,
                initialize: false,
                units,
                onlyCalculateOnButton,
                suggestJets,
            },
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("Custom_JettingApp_ShareSuccess"),
                    messageType: "success",
                });
            },
            onComplete() {
                setModalIsOpen(false);
                onEmailClick && onEmailClick();
            },
            onError() {
                toasterContext.addToast({
                    body: siteMessage("Custom_JettingApp_ShareFailedMessage"),
                    messageType: "danger",
                });
            },
        });
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? requiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setRecipientEmailError(errorMessage);
        return !errorMessage;
    };

    const resetFields = () => {
        setRecipientEmail("");
        setRecipientEmailError("");
        setInProgress(false);
    };

    const recipientEmailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        validateEmail(event.target.value);
        setRecipientEmail(event.target.value);
    };

    const onClickPrint = () => {
        openPrintDialog();
    };

    const copyLinkToClipboard = (link: string) => {
        const dummy = document.createElement("input");
        document.body.appendChild(dummy);

        const currentUrl = window.location.href;
        const queryParams = new URLSearchParams(location.search);
        queryParams.set("savedConfigId", link);
        if (calculatorIdFromState) {
            queryParams.set("calculatorId", calculatorIdFromState);
        }

        const urlObj = new URL(currentUrl);
        urlObj.search = queryParams.toString();
        const copiedLink = urlObj.toString();

        dummy.value = copiedLink;
        dummy.select();
        document.execCommand("copy");
        document.body.removeChild(dummy);
        toasterContext.addToast({
            body: siteMessage("Custom_JettingApp_CopyLinkSuccess"),
            messageType: "info",
        });
    };

    const onClickCopy = () => {
        setIsCopyInProgress(true);
        const suggestJets = parseBoolean(localStorage.getItem("jettingAppSuggestJets") || "false");
        copyCalculatorConfiguration({
            id: calculatorId,
            parameter: { panels, changeUnits: false, initialize: false, units, onlyCalculateOnButton, suggestJets },
            onSuccess: result => {
                copyLinkToClipboard(result.id);
                setIsCopyInProgress(false);
            },
        });
        onCopyClick && onCopyClick();
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link {...styles.emailLink} onClick={onClickShare}>
                {translate("Email")}
            </Link>
            <Link {...styles.printLink} onClick={onClickPrint}>
                {translate("Print")}
            </Link>
            <Link disabled={isCopyInProgress} {...styles.copyLink} onClick={onClickCopy}>
                {translate("Copy Link")}
            </Link>
            <Modal
                {...styles.modal}
                headline={translate("Share Product")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper {...styles.container} data-test-selector="jettingAppShareConfigurationModal">
                    <TextField
                        label={translate("Recipient Email")}
                        value={recipientEmail}
                        error={recipientEmailError}
                        onChange={recipientEmailChangeHandler}
                        data-test-selector="jettingAppRecipientEmail"
                        maxLength={254}
                        requiredLabel=" "
                        {...styles.recipientEmailTextField}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        variant="secondary"
                        showIcon={false}
                        onClick={modalCloseHandler}
                        data-test-selector="jettingAppRecipientCancel"
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        showIcon={false}
                        onClick={shareButtonClickHandler}
                        data-test-selector="jettingAppRecipientSubmit"
                        disabled={inProgress || disableSubmitButton}
                    >
                        {translate("Share")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(JettinAppActions);
